import { faArrowsRotate } from '@fortawesome/pro-solid-svg-icons';
import type { AppBuildStampModel } from '@supply-mover-2-0/core';
import { useEffect, useState, type ReactElement } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import {
	AppUpdateService,
	type AppUpdateStateModel,
} from '../../services/AppUpdateService';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';

/**
 * How long the post-update confirmation strip stays up. It runs longer when
 * it carries the What's New link, because a link the reader has to notice,
 * read and click needs more than the glance a bare confirmation does.
 */
const JUST_UPDATED_MS = 6000;
const JUST_UPDATED_WITH_LINK_MS = 15000;

interface AppUpdateBannerProps {
	/**
	 * The build stamp the bundle was compiled with (the app's vite version
	 * plugin defines it). Null — e.g. dev — renders nothing and starts no
	 * watcher.
	 */
	buildStamp: AppBuildStampModel | null;
	/**
	 * The product name spoken in the banner ("Workd"). Omit on white-label
	 * surfaces (the storefront) — the copy then stays brand-neutral.
	 */
	productName?: string;
	/**
	 * Navigates to the release digest ("What's New" in the Application
	 * Guide). Supplied only where such a page exists — the CRM — so the
	 * confirmation strip can offer a link to what actually changed. Omitted
	 * elsewhere (the storefront has no customer-facing guide), and the strip
	 * then reads exactly as it did before.
	 */
	onWhatsNew?: () => void;
}

/**
 * Top-of-screen notice that a newer build is deployed, with the watcher's
 * lifecycle: mounting this (once, in the app shell) starts AppUpdateService,
 * shows the post-update toast when a reload brought the tab current, and
 * renders the banner while an update is pending. The service applies the
 * update itself after a network-quiet stretch; the button is for the user
 * who wants it now.
 */
export function AppUpdateBanner({
	buildStamp,
	productName,
	onWhatsNew,
}: AppUpdateBannerProps): ReactElement | null {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const [state, setState] = useState<AppUpdateStateModel>(
		AppUpdateService.getState(),
	);
	const [justUpdated, setJustUpdated] = useState(false);

	useEffect(() => {
		AppUpdateService.start(buildStamp);
		if (AppUpdateService.consumeJustUpdatedFlag()) {
			// Rendered by this component rather than toasted: the app (and its
			// ToastContainer) can still be booting when this mounts, and a
			// toast with no container yet is silently dropped.
			setJustUpdated(true);
			setTimeout(
				() => {
					setJustUpdated(false);
				},
				onWhatsNew != null ? JUST_UPDATED_WITH_LINK_MS : JUST_UPDATED_MS,
			);
		}
		return AppUpdateService.subscribe(setState);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (justUpdated && !state.updateAvailable) {
		return (
			<div
				role="status"
				className={cn(
					'flex items-center justify-center gap-3 px-4 py-1.5 text-sm border-b',
					colors.classes.borderDefault,
				)}
				style={{
					backgroundColor: colors.values.success,
					color: '#ffffff',
				}}
			>
				<Icon icon={faArrowsRotate} />
				<span>
					{productName != null
						? t(`${productName} has been updated to the latest version.`)
						: t('Updated to the latest version.')}
				</span>
				{onWhatsNew != null && (
					<button
						type="button"
						onClick={() => {
							setJustUpdated(false);
							onWhatsNew();
						}}
						className="underline font-semibold"
					>
						{t("See what's new")}
					</button>
				)}
			</div>
		);
	}

	if (!state.updateAvailable) {
		return null;
	}

	return (
		<div
			role="status"
			className={cn(
				'flex items-center justify-center gap-3 px-4 py-1.5 text-sm border-b',
				colors.classes.borderDefault,
			)}
			style={{
				backgroundColor: colors.values.primaryDark,
				color: '#ffffff',
			}}
		>
			<Icon icon={faArrowsRotate} />
			<span>
				{state.reloadPending
					? t(
							`A new version${productName != null ? ` of ${productName}` : ''} is available — it will apply automatically in a moment.`,
						)
					: t(
							`A new version${productName != null ? ` of ${productName}` : ''} is available.`,
						)}
			</span>
			<button
				type="button"
				onClick={() => {
					AppUpdateService.applyNow();
				}}
				className="underline font-semibold"
			>
				{t('Refresh now')}
			</button>
		</div>
	);
}
